import React, { useMemo, useState } from "react";
import Cookies from "js-cookie";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import useFetch from "../../../../hooks/useFetch";
import {
    PageHeader,
    EmptyState,
    Skeleton,
} from "../../../shares/dashboard/DashboardWidgets";

/**
 * The student's assigned booklets.
 *
 * This page previously fetched management/users/:id/user_books/ into state and
 * then rendered an empty <div> - the data arrived and was never displayed, so
 * the route showed a blank card. It now renders the list.
 *
 * The endpoint returns either a bare array or a paginated { results } envelope
 * depending on the caller, so both are unwrapped.
 */
const Books = () => {
    const [books, setBooks] = useState(null);
    const userId = Cookies.get("userId");

    const getBooks = useFetch({
        method: "GET",
        url: `management/users/${userId}/user_books/`,
        noHeader: false,
        trigger: true,
        setter: setBooks,
    });

    const list = useMemo(() => {
        if (Array.isArray(books)) return books;
        if (Array.isArray(books?.results)) return books.results;
        return [];
    }, [books]);

    const isLoading = getBooks.loading && !books;

    return (
        <div className="flex flex-col gap-xl pb-4xl">
            <PageHeader
                title="جزوات"
                description="جزوه‌هایی که برای تو ثبت شده"
                icon={LibraryBooksIcon}
            />

            {isLoading ? (
                <div className="grid grid-cols-3 gap-lg max-md:grid-cols-2 max-sm:grid-cols-1">
                    {[0, 1, 2].map((key) => (
                        <Skeleton key={key} className="h-[11rem] w-full" />
                    ))}
                </div>
            ) : list.length === 0 ? (
                <EmptyState
                    icon={LibraryBooksIcon}
                    title="جزوه‌ای برات ثبت نشده"
                    description="وقتی جزوه‌ای به حساب تو اضافه بشه، همین‌جا فهرست می‌شود."
                />
            ) : (
                <ul className="grid grid-cols-3 gap-lg max-md:grid-cols-2 max-sm:grid-cols-1">
                    {list.map((book, index) => (
                        <li
                            key={book.id ?? book.booksname ?? index}
                            style={{ "--i": index }}
                            className="dash-rise flex items-center gap-md rounded-panel border border-rule-subtle bg-surface p-lg shadow-card"
                        >
                            <span className="flex h-[4.4rem] w-[4.4rem] shrink-0 items-center justify-center rounded-card bg-primary-soft text-primary">
                                <MenuBookIcon sx={{ fontSize: "2.2rem" }} />
                            </span>
                            <span className="min-w-0 text-base font-medium text-ink">
                                {book.booksname ?? "جزوه"}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Books;
